// Project settings, currencies and issued estimates
import { CCY_SYM, CUR, SHARED_FX, baseRates, ccySym, effFx, grandTotal, projectCcy } from '../core/budget.js';
import { h, useEffect, useState } from '../core/react.js';
import { MONO, T, fmt } from '../core/theme.js';
import { Pill } from '../ui/widgets.js';
import { Icon } from '../ui/icons.js';
import { downloadEstimatePDF } from './estimate-pdf.js';
import { btnStyle, inputStyle } from '../ui/widgets.js';
import { clearDeviceData } from '../core/util.js';
import { API } from '../core/api.js';
import { loadJsPDF } from '../core/excel.js';
import { isoToday } from '../core/dates.js';

export function Settings({
  project,
  fx,
  setFx,
  user
}) {
  const canEdit = !['Read-only', 'Freelancer'].includes(user?.role);
  const [changed, setChanged] = useState([]);   // currencies just refreshed from the ECB, highlighted in the table
  const [saved, setSaved] = useState(false);
  async function saveFX() {
    try {
      await window.PrismAPI.call('settings.save', {
        key: 'fx',
        data: fx
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      window.alert('Rates not saved: ' + e.message);
    }
  }
  return React.createElement("div", {
    style: {
      padding: '30px 34px',
      overflowY: 'auto',
      height: '100%',
      background: T.ivory
    },
    className: "fade-up"
  }, React.createElement("div", {
    style: {
      background: T.white,
      border: `1px solid ${T.rule}`,
      padding: '16px 18px',
      marginBottom: 20,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 16
    }
  }, React.createElement("div", null, React.createElement("div", {
    style: {
      fontSize: 13,
      fontWeight: 600,
      color: T.coal,
      marginBottom: 3
    }
  }, "This device"), React.createElement("div", {
    style: {
      fontSize: 12,
      color: T.mist,
      lineHeight: 1.5
    }
  }, "Projects and rates are stored on the server. This device only remembers your last project and screen layout. Clear it and sign out before handing over a shared computer.")), React.createElement("button", {
    onClick: clearDeviceData,
    style: {
      flexShrink: 0,
      padding: '8px 14px',
      border: `1px solid ${T.redBr}`,
      color: T.red,
      background: T.redBg,
      fontSize: 11,
      fontWeight: 600
    }
  }, "Clear and sign out")), React.createElement("div", {
    style: {
      fontSize: 20,
      fontWeight: 600,
      color: T.coal,
      marginBottom: 28,
      letterSpacing: '-0.02em'
    }
  }, "Settings"), React.createElement("div", {
    className: 'grid-stack',
    style: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 22,
      maxWidth: 940
    }
  }, React.createElement("div", {
    style: {
      background: T.white,
      padding: '24px',
      boxShadow: '0 1px 4px rgba(62,63,67,.06)'
    }
  }, React.createElement("div", {
    style: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.12em',
      color: T.mist,
      marginBottom: 18
    }
  }, "PROJECT DETAILS"), [['Project Name', project?.name || '—'], ['Client', project?.client || '—'], ['Event Date', project?.date || '\u2014'], ['Status', project?.status || '\u2014'], ['Cost currency', CUR.cost], ['Billing currency', CUR.bill]].map(([l, v]) => React.createElement("div", {
    key: l,
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '9px 0',
      borderBottom: `1px solid ${T.rule}`
    }
  }, React.createElement("span", {
    style: {
      fontSize: 12,
      color: T.mist
    }
  }, l), React.createElement("span", {
    style: {
      fontSize: 12,
      color: T.coal,
      fontWeight: 500,
      maxWidth: 220,
      textAlign: 'right',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  }, v)))), React.createElement("div", {
    style: {
      background: T.white,
      padding: '24px',
      boxShadow: '0 1px 4px rgba(62,63,67,.06)'
    }
  }, React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 18
    }
  }, React.createElement("div", {
    style: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.12em',
      color: T.mist
    }
  }, "FX RATES"), React.createElement(Pill, {
    label: "Shared with team",
    color: T.green,
    bg: T.greenBg
  })), React.createElement("div", {
    style: { fontSize: 11.5, color: T.slate, marginBottom: 12 }
  }, "Used by every project that hasn't locked its own rates, and by Tenders. Projects lock their rates on Project settings, or automatically when a client estimate is issued."), canEdit && h(EcbRefresh, { fx, setFx, user, onApplied: setChanged }), React.createElement(RatesTable, {
    rates: fx,
    onChange: setFx,
    highlight: changed
  }), React.createElement("div", {
    style: {
      display: 'flex',
      gap: 10,
      marginTop: 14,
      alignItems: 'center'
    }
  }, React.createElement("button", {
    onClick: saveFX,
    style: {
      flex: 1,
      padding: '9px',
      background: T.navy,
      color: T.white,
      border: 'none',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.08em',
      cursor: 'pointer'
    }
  }, "SAVE RATES"), saved && React.createElement("span", {
    style: {
      fontSize: 11,
      color: T.green,
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: 4
    }
  }, React.createElement(Icon.check, {
    s: 12
  }), "Saved")))));
}
// ── Issued estimates: every client PDF is kept as it was sent, and can be downloaded again or compared
// ── Refresh from the European Central Bank: shows each change first, then applies and saves.
// Currencies the ECB doesn't publish (e.g. AED) keep their rate; projects with locked rates aren't affected.
function EcbRefresh({ fx, setFx, user, onApplied }) {
  const [info, setInfo] = useState(null);       // the last refresh: { date, by, at }
  const [busy, setBusy] = useState(false);
  const [offer, setOffer] = useState(null);     // { date, usd } from the ECB, waiting to be applied
  const [msg, setMsg] = useState(null);
  useEffect(() => { API.call('settings.get', { key: 'fxinfo' }).then(r => setInfo(r.data)).catch(() => {}); }, []);
  const day = d => new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  async function fetchRates() {
    setBusy(true); setMsg(null);
    try { setOffer(await API.call('fx.ecb', {})); } catch (e) { setMsg({ err: true, text: e.message }); }
    setBusy(false);
  }
  const mine = Object.keys(fx || {}).filter(c => c !== 'USD').sort();
  const rows = offer ? mine.filter(c => offer.usd[c]).map(c => ({ c, now: Number(fx[c]?.usd) || 0, ecb: offer.usd[c] })) : [];
  const kept = offer ? mine.filter(c => !offer.usd[c]) : [];
  const extra = offer ? Object.keys(offer.usd).filter(c => !fx[c]).sort() : [];
  async function apply() {
    const next = { ...fx };
    rows.forEach(r => { next[r.c] = { ...(next[r.c] || {}), usd: r.ecb }; });
    const meta = { source: 'European Central Bank', date: offer.date, by: user?.name || '', at: new Date().toISOString() };
    setBusy(true);
    try {
      await API.call('settings.save', { key: 'fx', data: next });
      await API.call('settings.save', { key: 'fxinfo', data: meta });
      setFx(next); setInfo(meta); onApplied(rows.filter(r => Math.abs(r.ecb - r.now) > 1e-9).map(r => r.c));
      setMsg({ text: `${rows.length} rate${rows.length === 1 ? '' : 's'} updated to the ECB's of ${day(offer.date)} and saved for the team.` }); setOffer(null);
    } catch (e) { setMsg({ err: true, text: 'Rates not saved: ' + e.message }); }
    setBusy(false);
  }
  const pct = r => r.now ? (r.ecb - r.now) / r.now : null;
  const cell = { padding: '5px 8px', fontSize: 11.5, borderBottom: `1px solid ${T.ivoryD}`, ...MONO };
  return h("div", { style: { border: `1px solid ${T.rule}`, background: T.ivory, padding: '12px 14px', marginBottom: 14 } },
    h("div", { style: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
      h("div", { style: { flex: 1, minWidth: 180, fontSize: 11.5, color: T.slate, lineHeight: 1.5 } },
        info?.date ? `Last refreshed from the European Central Bank: rates of ${day(info.date)}${info.by ? ', by ' + info.by : ''}.` : 'Update the table to the European Central Bank\'s reference rates, published each working day around 4pm CET.'),
      !offer && h("button", { onClick: fetchRates, disabled: busy, style: btnStyle(false) }, busy ? 'Asking the ECB…' : 'Refresh from the European Central Bank')),
    offer && h("div", { style: { marginTop: 12 } },
      h("div", { style: { fontSize: 12, fontWeight: 600, color: T.coal, marginBottom: 6 } }, `ECB reference rates of ${day(offer.date)}: 1 unit in US$`),
      h("table", { style: { width: '100%', borderCollapse: 'collapse', background: T.white } },
        h("thead", null, h("tr", null, ['Currency', 'Now', 'ECB', 'Change'].map((t, i) => h("th", { key: t, style: { ...cell, fontFamily: undefined, textAlign: i ? 'right' : 'left', fontSize: 10, color: T.mist, fontWeight: 700 } }, t)))),
        h("tbody", null, rows.map(r => { const p = pct(r), big = p != null && Math.abs(p) >= 0.05;
          return h("tr", { key: r.c, style: { background: big ? T.amberBg : undefined } },
            h("td", { style: { ...cell, fontWeight: 700, color: T.navy } }, r.c),
            h("td", { style: { ...cell, textAlign: 'right', color: T.mist } }, r.now ? r.now.toFixed(4) : '—'),
            h("td", { style: { ...cell, textAlign: 'right', color: T.coal, fontWeight: 600 } }, r.ecb.toFixed(4)),
            h("td", { style: { ...cell, textAlign: 'right', color: p == null || Math.abs(p) < 0.0005 ? T.mist : p > 0 ? T.green : T.red, fontWeight: big ? 700 : 400 } },
              p == null ? 'new' : Math.abs(p) < 0.0005 ? 'same' : (p > 0 ? '+' : '') + (p * 100).toFixed(1) + '%')); }))),
      kept.length > 0 && h("div", { style: { fontSize: 11, color: T.slate, marginTop: 8 } }, `Not published by the ECB, kept as they are: ${kept.join(', ')}.`),
      extra.length > 0 && h("div", { style: { fontSize: 11, color: T.mist, marginTop: 4 } }, `The ECB also publishes ${extra.join(', ')}. Add any you need with Add currency below, then refresh again.`),
      rows.some(r => { const p = pct(r); return p != null && Math.abs(p) >= 0.05; }) && h("div", { style: { fontSize: 11, color: T.amber, marginTop: 4, fontWeight: 600 } }, 'Rows in amber move by 5% or more: worth a second look.'),
      h("div", { style: { fontSize: 11, color: T.mist, marginTop: 4 } }, 'Projects with locked rates keep theirs; this changes the team\'s rates that the others follow.'),
      h("div", { style: { display: 'flex', gap: 8, marginTop: 10 } },
        h("button", { onClick: apply, disabled: busy || !rows.length, style: btnStyle(true) }, busy ? 'Saving…' : `Apply ${rows.length} rate${rows.length === 1 ? '' : 's'} and save`),
        h("button", { onClick: () => setOffer(null), disabled: busy, style: btnStyle(false) }, 'Cancel'))),
    msg && h("div", { role: 'status', style: { fontSize: 11.5, marginTop: 8, color: msg.err ? T.red : T.green, fontWeight: 600 } }, msg.text));
}
// ── Exchange-rate table: what 1 unit of each currency is worth in US dollars
function RatesTable({ rates, onChange, readOnly, highlight = [] }) {
  const [code, setCode] = useState('');
  const [val, setVal] = useState('');
  const rows = Object.entries(rates || {}).sort(([a], [b]) => a.localeCompare(b));
  const set = (c, v) => onChange(prev => ({ ...prev, [c]: { ...(prev[c] || {}), usd: v } }));
  const remove = c => { if (window.confirm(`Remove ${c}? Lines in ${c} will show as having no rate.`)) onChange(prev => { const n = { ...prev }; delete n[c]; return n; }); };
  const add = () => {
    const c = code.trim().toUpperCase(), v = parseFloat(val);
    if (!/^[A-Z]{3}$/.test(c)) return window.alert('Use a three-letter currency code, like MXN.');
    if (!(v > 0)) return window.alert('Enter what 1 ' + c + ' is worth in US dollars.');
    onChange(prev => ({ ...prev, [c]: { usd: v } })); setCode(''); setVal('');
  };
  const inp = { ...MONO, width: 96, padding: '5px 8px', border: `1px solid ${T.rule}`, fontSize: 11, background: T.white, color: T.coal, borderRadius: 0 };
  return h("div", null,
    h("table", { style: { width: '100%', borderCollapse: 'collapse' } },
      h("thead", null, h("tr", { style: { background: T.navy } }, ['Currency', '1 unit in US$', ''].map((t, i) => h("th", { key: i, style: { padding: '7px 10px', textAlign: 'left', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em', color: 'rgba(255,255,255,.65)' } }, t)))),
      h("tbody", null, rows.map(([c, r], i) => h("tr", { key: c, style: { background: highlight.includes(c) ? T.amberBg : i % 2 === 0 ? T.white : T.ivory } },
        h("td", { style: { padding: '6px 10px', fontSize: 12, fontWeight: 700, color: T.navy, ...MONO } }, c),
        h("td", { style: { padding: '4px 6px' } }, c === 'USD' ? h("span", { style: { ...MONO, fontSize: 11, color: T.mist, paddingLeft: 8 } }, '1') :
          h("input", { type: 'number', value: r?.usd ?? '', step: '0.0001', min: 0, disabled: readOnly, onChange: e => set(c, parseFloat(e.target.value) || 0), style: inp })),
        h("td", { style: { textAlign: 'right', paddingRight: 6 } }, !readOnly && c !== 'USD' && h("button", { onClick: () => remove(c), title: 'Remove ' + c, 'aria-label': 'Remove ' + c, style: { background: 'none', border: 'none', color: T.mist, cursor: 'pointer' } }, '\u00D7')))))),
    !readOnly && h("div", { style: { display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' } },
      h("input", { placeholder: 'Code', value: code, maxLength: 3, onChange: e => setCode(e.target.value.toUpperCase()), style: { ...inp, width: 64 } }),
      h("input", { placeholder: 'in US$', type: 'number', step: '0.0001', value: val, onChange: e => setVal(e.target.value), style: inp }),
      h("button", { onClick: add, style: { ...btnStyle(false), padding: '5px 10px', fontSize: 11 } }, 'Add currency')));
}
// ── Project currencies and locked rates (Project settings page)
export function CurrencyCard({ project, user, updateProject, card }) {
  const readOnly = user?.role === 'Read-only';
  const { cost, bill } = projectCcy(project);
  const base = baseRates(project);
  const codes = Object.keys(base).sort();
  const totalWith = rates => grandTotal(project.sections || [], effFx({ ...project, fxLocked: true, fx: rates })).outUSD;
  const sel = (label, key, value) => h("label", { style: { display: 'flex', flexDirection: 'column', gap: 5, flex: '0 1 200px' } },
    h("span", { style: { fontSize: 11, color: T.slate, fontWeight: 600 } }, label),
    h("select", { value, disabled: readOnly, onChange: e => updateProject({ [key]: e.target.value }), style: inputStyle }, codes.map(c => h("option", { key: c }, c))));
  const lock = () => updateProject({ fxLocked: true, fx: JSON.parse(JSON.stringify(SHARED_FX)), fxLockedAt: isoToday(), fxLockedBy: user?.name || '' });
  const unlock = () => {
    const a = totalWith(project.fx), b = totalWith(SHARED_FX);
    if (window.confirm(`Follow the team's current rates instead of this project's locked ones?\n\nClient total: ${ccySym(bill)}${fmt.num(a, 2)} now → ${ccySym(bill)}${fmt.num(b, 2)} with team rates.\n\nEstimates already issued keep the prices they were sent with.`))
      updateProject({ fxLocked: false });
  };
  const refresh = () => {
    const a = totalWith(project.fx), b = totalWith(SHARED_FX);
    if (window.confirm(`Replace this project's locked rates with the team's current rates?\n\nClient total: ${ccySym(bill)}${fmt.num(a, 2)} → ${ccySym(bill)}${fmt.num(b, 2)}.`))
      updateProject({ fx: JSON.parse(JSON.stringify(SHARED_FX)), fxLockedAt: isoToday(), fxLockedBy: user?.name || '' });
  };
  const missing = grandTotal(project.sections || [], effFx(project)).missing;
  return card('Currencies and exchange rates', `Costs are tracked in the cost currency (the agency's P&L) and the client is billed in the billing currency. ${project.fxLocked ? '' : 'This project follows the team\'s rates in Settings; lock them when you agree prices with the client, so later rate changes can\'t move this budget.'}`,
    h("div", { style: { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14 } }, sel('Cost currency', 'costCcy', cost), sel('Billing currency', 'billCcy', bill)),
    missing.length > 0 && h("div", { style: { fontSize: 12, color: T.red, marginBottom: 12 } }, `Lines in ${missing.join(', ')} have no rate and count as zero until you add one${project.fxLocked ? ' below' : ' in Settings'}.`),
    project.fxLocked
      ? h("div", null,
          h("div", { style: { fontSize: 12, color: T.coal, marginBottom: 10 } }, `Rates locked on ${project.fxLockedAt || '—'}${project.fxLockedBy ? ' by ' + project.fxLockedBy : ''}. Changes here affect this project only.`),
          h("div", { style: { maxWidth: 420 } }, h(RatesTable, { rates: project.fx, readOnly, highlight: missing, onChange: fn => updateProject(p => ({ fx: fn(p.fx || {}) })) })),
          !readOnly && h("div", { style: { display: 'flex', gap: 8, marginTop: 12 } },
            h("button", { onClick: refresh, style: btnStyle(false) }, "Use today's team rates"),
            h("button", { onClick: unlock, style: btnStyle(false) }, 'Unlock')))
      : !readOnly && h("button", { onClick: lock, style: btnStyle(true) }, 'Lock rates for this project'));
}

export function IssuedEstimates({ project, current, flash, status }) {
  const [list, setList] = useState(null);
  const [cmp, setCmp] = useState(null);
  async function load() { try { setList((await API.call('estimates.list', { projectId: project.id })).estimates); } catch (e) { setList([]); } }
  useEffect(() => { load(); window.addEventListener('prism-estimate-saved', load); return () => window.removeEventListener('prism-estimate-saved', load); }, [project?.id]);
  async function open(id) { return (await API.call('estimate.get', { id })).estimate; }
  async function again(e) {
    try { const full = await open(e.id); await loadJsPDF(); downloadEstimatePDF(full.data.layout, full.data.filename || `${e.label}.pdf`); } catch (err) { flash(err.message); }
  }
  async function compare(e) {
    try {
      const full = await open(e.id), old = full.data.lines || [], now = current.lines || [];
      const key = l => l.itemId || l.desc;
      const om = new Map(old.map(l => [key(l), l])), nm = new Map(now.map(l => [key(l), l]));
      const rows = [];
      now.forEach(l => { const o = om.get(key(l)); if (!o) rows.push({ kind: 'Added', desc: l.desc, was: null, now: l.amt }); else if (o.amt !== l.amt || o.desc !== l.desc) rows.push({ kind: 'Changed', desc: l.desc + (o.desc !== l.desc ? ` (was "${o.desc}")` : ''), was: o.amt, now: l.amt }); });
      old.forEach(l => { if (!nm.has(key(l))) rows.push({ kind: 'Removed', desc: l.desc, was: l.amt, now: null }); });
      setCmp({ e, rows, was: full.total, now: current.total, ccyChanged: full.ccy !== current.ccy ? full.ccy : null });
    } catch (err) { flash(err.message); }
  }
  const money = (n, c) => n == null ? '—' : (CCY_SYM[c] || (c + ' ')) + fmt.num(n, 2);
  const cell = { padding: '5px 4px', fontSize: 11, verticalAlign: 'top' };
  return h("div", { style: { marginTop: 18, borderTop: `1px solid ${T.rule}`, paddingTop: 14 } },
    h("div", { style: { fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em', color: T.mist, marginBottom: 8 } }, "ISSUED ESTIMATES"),
    !list ? h("div", { style: { fontSize: 11, color: T.mist } }, 'Loading…') :
      !list.length ? h("div", { style: { fontSize: 11, color: T.mist } }, 'None yet. Each client PDF you download is kept here exactly as sent; mark it approved on Approvals & POs.') :
      list.map(e => h("div", { key: e.id, style: { padding: '8px 0', borderBottom: `1px solid ${T.ivoryD}` } },
        h("div", { style: { display: 'flex', justifyContent: 'space-between', gap: 8 } },
          h("b", { style: { fontSize: 12, fontWeight: 600, color: T.coal } }, (e.kind === 'change' ? 'Change order ' : '') + e.label,
            status?.[e.id] === 'approved' && h("span", { style: { fontSize: 10, color: T.green, marginLeft: 6 } }, '✓ approved')),
          h("span", { style: { ...MONO, fontSize: 11, color: T.coal } }, (e.kind === 'change' && e.total > 0 ? '+' : '') + money(e.total, e.ccy))),
        h("div", { style: { fontSize: 10.5, color: T.mist, marginTop: 2 } }, `${new Date(e.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · ${e.created_by}`),
        h("div", { style: { display: 'flex', gap: 6, marginTop: 6 } },
          h("button", { onClick: () => again(e), style: { ...btnStyle(false), padding: '4px 9px', fontSize: 10.5 } }, 'Download again'),
          e.kind !== 'change' && h("button", { onClick: () => compare(e), style: { ...btnStyle(false), padding: '4px 9px', fontSize: 10.5 } }, 'Compare with now')))),
    cmp && h("div", { style: { marginTop: 12, padding: 12, background: T.ivory, border: `1px solid ${T.rule}` } },
      h("div", { style: { display: 'flex', justifyContent: 'space-between' } },
        h("b", { style: { fontSize: 12, fontWeight: 600 } }, `Since ${cmp.e.label}`),
        h("button", { onClick: () => setCmp(null), style: { background: 'none', border: 'none', cursor: 'pointer', color: T.mist } }, '\u00D7')),
      cmp.ccyChanged && h("div", { style: { fontSize: 11, color: T.amber, margin: '6px 0' } }, `That version was in ${cmp.ccyChanged}; the budget now bills in ${current.ccy}, so amounts aren't directly comparable.`),
      h("div", { style: { fontSize: 11.5, margin: '6px 0 8px', color: T.slate } },
        `Total ${money(cmp.was, cmp.e.ccy)} → ${money(cmp.now, current.ccy)}` + (cmp.ccyChanged ? '' : ` (${cmp.now - cmp.was >= 0 ? '+' : ''}${fmt.num(cmp.now - cmp.was, 2)})`)),
      !cmp.rows.length ? h("div", { style: { fontSize: 11, color: T.green } }, 'No line changes.') :
        h("table", { style: { width: '100%', borderCollapse: 'collapse' } }, h("tbody", null, cmp.rows.map((r, i) => h("tr", { key: i, style: { borderTop: `1px solid ${T.ivoryD}` } },
          h("td", { style: { ...cell, color: r.kind === 'Removed' ? T.red : r.kind === 'Added' ? T.green : T.amber, whiteSpace: 'nowrap' } }, r.kind),
          h("td", { style: { ...cell, color: T.coal } }, r.desc),
          h("td", { style: { ...cell, ...MONO, textAlign: 'right', whiteSpace: 'nowrap', color: T.mist } }, r.was == null ? '' : fmt.num(r.was, 2)),
          h("td", { style: { ...cell, ...MONO, textAlign: 'right', whiteSpace: 'nowrap' } }, r.now == null ? '' : fmt.num(r.now, 2))))))));
}
